import { useEffect, useState } from "react"
import { useParams, useLocation } from "wouter"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ArrowLeft, Download, FileImage, FileSpreadsheet, Presentation, RefreshCw, CheckCircle, XCircle, Clock } from "lucide-react"
import { useToast } from "@/hooks/use-toast"

interface ExportStatus {
  type: string
  status: 'idle' | 'loading' | 'success' | 'error'
  message: string
  timestamp?: string
}

export default function BlobPreviewScreen() {
  const params = useParams()
  const [, navigate] = useLocation()
  const { toast } = useToast()
  const projectId = params.projectId

  const [blob, setBlob] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exportStatuses, setExportStatuses] = useState<ExportStatus[]>([])

  useEffect(() => {
    if (projectId) {
      loadProjectBlob()
    }
  }, [projectId])

  const loadProjectBlob = async () => {
    try {
      setLoading(true)
      setError(null)

      // Get project data
      const projectResponse = await fetch(`/api/projects/${projectId}`)
      if (!projectResponse.ok) {
        throw new Error('Failed to load project data')
      }
      const projectData = await projectResponse.json()

      // Get ValueAssessment data from context/API
      const valueSummaryResponse = await fetch(`/api/projects/${projectId}/value-summary-blob`)
      let valueSummaryData = {}
      
      if (valueSummaryResponse.ok) {
        valueSummaryData = await valueSummaryResponse.json()
      }

      // Construct comprehensive blob
      const comprehensiveBlob = {
        // Project metadata
        projectId,
        projectName: projectData.name,
        clientName: projectData.client_org,
        status: projectData.status,
        createdAt: projectData.created_at,
        updatedAt: projectData.updated_at,
        
        // Project configuration
        selectedPillars: projectData.selected_pillars || [],
        legalEntityCount: projectData.legal_entity_count,
        revenueBand: projectData.revenue_band,
        roiConfig: projectData.roi_config || {},
        kickoffOverview: projectData.kickoff_overview || {},
        
        // Value Assessment data
        valueSummary: valueSummaryData,
        
        // Module completion
        moduleCompletion: projectData.module_completion || {},
        
        // Collaboration data
        collaborators: projectData.collaborators || [],
        
        // Export metadata
        exportGenerated: new Date().toISOString(),
        exportVersion: "1.0"
      }

      setBlob(comprehensiveBlob)
    } catch (err) {
      console.error('Error loading project blob:', err)
      setError(err instanceof Error ? err.message : 'Failed to load project data')
    } finally {
      setLoading(false)
    }
  }

  const updateExportStatus = (type: string, status: ExportStatus['status'], message: string) => {
    setExportStatuses(prev => {
      const filtered = prev.filter(s => s.type !== type)
      return [...filtered, {
        type,
        status,
        message,
        timestamp: new Date().toLocaleTimeString()
      }]
    })
  }

  const triggerExport = async (type: 'pptx' | 'excel' | 'images') => {
    updateExportStatus(type, 'loading', `Generating ${type.toUpperCase()}...`)

    try {
      let response: Response

      if (type === 'pptx') {
        response = await fetch('/api/export-pptx', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            projectId,
            valueSummary: blob
          })
        })

        if (response.ok) {
          const blob_data = await response.blob()
          const url = window.URL.createObjectURL(blob_data)
          const a = document.createElement('a')
          a.href = url
          a.download = `value_assessment_${projectId}_${Date.now()}.pptx`
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
          
          updateExportStatus(type, 'success', 'PowerPoint downloaded successfully')
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
      } else if (type === 'excel') {
        // TODO: Implement Excel export
        updateExportStatus(type, 'error', 'Excel export not yet implemented')
        return
      } else if (type === 'images') {
        // TODO: Implement image export
        updateExportStatus(type, 'error', 'Image export not yet implemented')
        return
      }

      toast({
        title: "Export Successful",
        description: `${type.toUpperCase()} export completed successfully`,
      })

    } catch (error) {
      console.error(`${type} export error:`, error)
      const errorMessage = error instanceof Error ? error.message : `Failed to generate ${type.toUpperCase()}`
      updateExportStatus(type, 'error', errorMessage)
      
      toast({
        title: "Export Failed",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  const getStatusIcon = (status: ExportStatus['status']) => {
    switch (status) {
      case 'loading':
        return <Clock className="w-4 h-4 text-yellow-500 animate-spin" />
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />
      default:
        return null
    }
  }

  const getStatusBadgeVariant = (status: ExportStatus['status']) => {
    switch (status) {
      case 'loading':
        return 'secondary'
      case 'success':
        return 'default'
      case 'error':
        return 'destructive'
      default:
        return 'outline'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-[#4AE2C9]" />
          <div className="text-lg text-white">Loading project blob...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/projects')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Button>
        </div>
        
        <div className="text-center py-12">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Error Loading Project</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button onClick={loadProjectBlob} className="bg-[#4AE2C9] text-[#0F172A] hover:bg-[#4AE2C9]/90">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/projects')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Blob Preview & Export Testing</h1>
            <p className="text-gray-400">Project: {blob?.projectName || projectId}</p>
          </div>
        </div>
        
        <Badge variant="outline" className="text-yellow-400 border-[#FF715A]">
          Super Admin Only
        </Badge>
      </div>

      {/* Export Controls */}
      <Card className="bg-[#1A2332] border-gray-600">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Download className="w-5 h-5" />
            Export Testing Panel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              onClick={() => triggerExport('pptx')}
              disabled={exportStatuses.find(s => s.type === 'pptx')?.status === 'loading'}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
            >
              <Presentation className="w-4 h-4" />
              Export PowerPoint
            </Button>
            
            <Button
              onClick={() => triggerExport('excel')}
              disabled={exportStatuses.find(s => s.type === 'excel')?.status === 'loading'}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export Excel
            </Button>
            
            <Button
              onClick={() => triggerExport('images')}
              disabled={exportStatuses.find(s => s.type === 'images')?.status === 'loading'}
              className="bg-purple hover:bg-purple text-white flex items-center gap-2"
            >
              <FileImage className="w-4 h-4" />
              Export Images
            </Button>
          </div>

          {/* Export Status */}
          {exportStatuses.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-300">Export Status</h4>
              {exportStatuses.map((exportStatus, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                  {getStatusIcon(exportStatus.status)}
                  <Badge variant={getStatusBadgeVariant(exportStatus.status)}>
                    {exportStatus.type.toUpperCase()}
                  </Badge>
                  <span className="text-sm text-gray-300 flex-1">{exportStatus.message}</span>
                  {exportStatus.timestamp && (
                    <span className="text-xs text-gray-500">{exportStatus.timestamp}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Blob Preview */}
      <Card className="bg-[#1A2332] border-gray-600">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Project Value Summary Blob
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-900 p-4 rounded-lg overflow-hidden">
            <pre className="text-xs text-gray-300 overflow-x-auto max-h-[70vh] whitespace-pre-wrap">
              {JSON.stringify(blob, null, 2)}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}